import React, { useEffect, useState } from "react";
import { api, money, fmtDate, exportExcel, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/CrudModule";
import { ShortOver } from "@/pages/DriverPortal";
import { UserPlus as UserPlusIcon, SealCheck as SealCheckIcon, Trash as TrashIcon, Key as KeyIcon, DownloadSimple as DownloadSimpleIcon, Eye as EyeIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DailyBalance() {
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [wOpen, setWOpen] = useState(false);
  const [wForm, setWForm] = useState({ name: "", password: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [viewPhoto, setViewPhoto] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const load = () => {
    api.get("/daily-balance").then((r) => setRows(r.data)).catch(() => {});
    api.get("/workers").then((r) => setWorkers(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const approve = async (id) => {
    await api.post(`/daily-balance/${id}/approve`);
    toast.success("Report approved"); load();
  };

  const openView = async (r) => {
    setViewRow(r);
    try { const res = await api.get(`/daily-balance/${r.id}`); setViewRow(res.data); } catch {}
  };

  const addWorker = async () => {
    try { await api.post("/workers", wForm); toast.success(`Login created for ${wForm.name}`); setWOpen(false); setWForm({ name: "", password: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetPw = async () => {
    try { await api.put(`/workers/${pwFor.id}/password`, { password: newPw }); toast.success("Password updated"); setPwFor(null); setNewPw(""); }
    catch { toast.error("Failed"); }
  };
  const delWorker = async (w) => {
    if (!window.confirm(`Remove login for ${w.name}?`)) return;
    await api.delete(`/workers/${w.id}`); toast.success("Removed"); load();
  };

  const pending = rows.filter((r) => r.status !== "approved").length;
  const totalShortOver = rows.reduce((s, r) => s + Number(r.short_over || 0), 0);
  const weekByDriver = {};
  rows.forEach((r) => {
    const diff = (Date.now() - new Date(r.date).getTime()) / 86400000;
    if (diff <= 7) {
      const w = weekByDriver[r.worker] || { gallons: 0, cash: 0, count: 0 };
      w.gallons += Number(r.gallons_sold || 0); w.cash += Number(r.cash_collected || 0) + Number(r.online_collected || 0); w.count += 1;
      weekByDriver[r.worker] = w;
    }
  });
  const weekRows = Object.entries(weekByDriver).map(([worker, v]) => ({ worker, ...v }));

  return (
    <div className="space-y-5 animate-fade-up">
      <div><h1 className="font-head text-2xl font-bold">Daily Butane Balance</h1><p className="text-muted-foreground text-sm">Review driver end-of-day submissions, approve reports & manage worker logins.</p></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Total Submissions</div><div className="font-head text-2xl font-bold mt-1">{rows.length}</div></div>
        <div className="border rounded-lg bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-slate-900 p-4"><div className="text-xs text-destructive">Pending Approval</div><div className="font-head text-2xl font-bold mt-1 text-destructive">{pending}</div></div>
        <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Net Short / Over</div><div className="font-head text-2xl font-bold mt-1"><ShortOver value={totalShortOver.toFixed(2)} /></div></div>
        <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Active Drivers</div><div className="font-head text-2xl font-bold mt-1">{workers.length}</div></div>
      </div>

      <Tabs defaultValue="reports">
        <TabsList>
          <TabsTrigger value="reports" data-testid="tab-db-reports">Submissions</TabsTrigger>
          <TabsTrigger value="workers" data-testid="tab-db-workers">Worker Logins</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-db-weekly">Weekly Totals</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-4 space-y-2">
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => exportExcel("daily_balance.xlsx", rows)}><DownloadSimpleIcon size={16} className="mr-1.5" />Export to Excel</Button></div>
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50">
                <TableHead>Worker</TableHead><TableHead>Date</TableHead>
                <TableHead className="text-right">Gallons</TableHead><TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Cash</TableHead>
                <TableHead className="text-right">Online</TableHead>
                <TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">To Submit</TableHead>
                <TableHead className="text-right">Short/Over</TableHead><TableHead>Status</TableHead><TableHead>Photo</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">No submissions yet</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid="db-report-row" className="hover:bg-secondary/40">
                    <TableCell className="py-2 font-medium">{r.worker}</TableCell>
                    <TableCell className="py-2">{fmtDate(r.date)}</TableCell>
                    <TableCell className="py-2 text-right">{r.gallons_sold}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.price_per_gallon)}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.expected_sales)}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.cash_collected)}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.online_collected || 0)}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.total_expenses)}</TableCell>
                    <TableCell className="py-2 text-right">{money(r.cash_to_submit)}</TableCell>
                    <TableCell className="py-2 text-right"><ShortOver value={r.short_over} /></TableCell>
                    <TableCell className="py-2"><StatusBadge value={r.status === "approved" ? "ok" : "pending"} /></TableCell>
                    <TableCell className="py-2">{(r.photo_count || 0) > 0
                      ? <button type="button" data-testid="db-photo-thumb" onClick={() => openView(r)} className="text-xs text-primary hover:underline">{r.photo_count} photo{r.photo_count > 1 ? "s" : ""}</button>
                      : <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell className="py-2 text-right">
                      <button onClick={() => openView(r)} data-testid="db-view" className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary align-middle mr-1" title="View full report"><EyeIcon size={17} /></button>
                      {r.status === "approved"
                        ? <span className="text-xs text-emerald-600">✓ {r.approved_by}</span>
                        : <Button size="sm" onClick={() => approve(r.id)} data-testid="db-approve"><SealCheckIcon size={15} className="mr-1" />Approve</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="workers" className="mt-4 space-y-2">
          <div className="flex justify-end"><Button onClick={() => setWOpen(true)} data-testid="add-worker"><UserPlusIcon size={16} className="mr-1.5" />Add Worker Login</Button></div>
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50"><TableHead>Driver Name (login)</TableHead><TableHead>Role</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {workers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No worker logins yet</TableCell></TableRow>}
                {workers.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="py-2 font-medium">{w.name}</TableCell>
                    <TableCell className="py-2"><StatusBadge value="active" /></TableCell>
                    <TableCell className="py-2">{fmtDate(w.created_at)}</TableCell>
                    <TableCell className="py-2 text-right">
                      <button onClick={() => { setPwFor(w); setNewPw(""); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Reset password"><KeyIcon size={16} /></button>
                      <button onClick={() => delWorker(w)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><TrashIcon size={16} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">Drivers log in at the same login page using their <b>name</b> and the password you set here. They only see the Daily Balance form and their own submissions.</p>
        </TabsContent>

        <TabsContent value="weekly" className="mt-4">
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/50"><TableHead>Driver</TableHead><TableHead className="text-right">Submissions (7d)</TableHead><TableHead className="text-right">Gallons Sold</TableHead><TableHead className="text-right">Cash Submitted</TableHead></TableRow></TableHeader>
              <TableBody>
                {weekRows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No activity in the last 7 days</TableCell></TableRow>}
                {weekRows.map((w) => (
                  <TableRow key={w.worker}><TableCell className="py-2 font-medium">{w.worker}</TableCell><TableCell className="py-2 text-right">{w.count}</TableCell><TableCell className="py-2 text-right">{w.gallons.toFixed(1)}</TableCell><TableCell className="py-2 text-right">{money(w.cash)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* add worker */}
      <Dialog open={wOpen} onOpenChange={setWOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Add Worker Login</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Driver Name (used to log in)</Label><Input data-testid="worker-name" value={wForm.name} onChange={(e) => setWForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Carlos Mendez" /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Password</Label><Input data-testid="worker-password" value={wForm.password} onChange={(e) => setWForm((f) => ({ ...f, password: e.target.value }))} placeholder="Set a password" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setWOpen(false)}>Cancel</Button><Button data-testid="worker-save" onClick={addWorker} disabled={!wForm.name || !wForm.password}>Create Login</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reset pw */}
      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Reset Password — {pwFor?.name}</DialogTitle></DialogHeader>
          <div className="py-2"><Label className="text-xs text-muted-foreground mb-1.5 block">New Password</Label><Input value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPwFor(null)}>Cancel</Button><Button onClick={resetPw} disabled={!newPw}>Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* meter photo viewer */}
      <Dialog open={!!viewPhoto} onOpenChange={(o) => !o && setViewPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-head">Meter Photo</DialogTitle><DialogDescription>Proof of pump meter reading submitted by the driver.</DialogDescription></DialogHeader>
          {viewPhoto && <img src={viewPhoto} alt="meter proof" data-testid="db-photo-full" className="w-full max-h-[75vh] object-contain rounded-md" />}
        </DialogContent>
      </Dialog>

      {/* full report viewer */}
      <Dialog open={!!viewRow} onOpenChange={(o) => !o && setViewRow(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-auto" data-testid="db-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">Daily Balance — {viewRow?.worker}
              <StatusBadge value={viewRow?.status === "approved" ? "ok" : "pending"} />
            </DialogTitle>
            <DialogDescription>Submitted {viewRow ? fmtDate(viewRow.date) : ""}{viewRow?.approved_by ? ` · Approved by ${viewRow.approved_by}` : ""}</DialogDescription>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="border rounded-lg p-3 bg-secondary/30"><div className="text-[11px] text-muted-foreground">Meter Started</div><div className="font-head font-semibold">{viewRow.meter_start}</div></div>
                <div className="border rounded-lg p-3 bg-secondary/30"><div className="text-[11px] text-muted-foreground">Meter Ended</div><div className="font-head font-semibold">{viewRow.meter_end}</div></div>
                <div className="border rounded-lg p-3 bg-secondary/30"><div className="text-[11px] text-muted-foreground">Gallons Sold</div><div className="font-head font-semibold">{viewRow.gallons_sold}</div></div>
              </div>

              <div>
                <div className="text-sm font-medium mb-1.5">Price Breakdown</div>
                <Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead className="text-right">Gallons</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(viewRow.price_tiers || []).map((t, i) => (
                      <TableRow key={i}><TableCell className="py-1.5">{t.label}</TableCell><TableCell className="py-1.5 text-right">{t.gallons}</TableCell><TableCell className="py-1.5 text-right">{money(t.price)}</TableCell><TableCell className="py-1.5 text-right">{money(Number(t.gallons) * Number(t.price))}</TableCell></TableRow>
                    ))}
                    {(!viewRow.price_tiers || viewRow.price_tiers.length === 0) && <TableRow><TableCell colSpan={4} className="py-2 text-muted-foreground text-sm">No breakdown</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>

              <div>
                <div className="text-sm font-medium mb-1.5">Expenses</div>
                <Table><TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(viewRow.expenses || []).map((e, i) => <TableRow key={i}><TableCell className="py-1.5">{e.description || "-"}</TableCell><TableCell className="py-1.5 text-right text-destructive">{money(e.amount)}</TableCell></TableRow>)}
                    {(!viewRow.expenses || viewRow.expenses.length === 0) && <TableRow><TableCell colSpan={2} className="py-2 text-muted-foreground text-sm">No expenses</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>

              <div className="border rounded-lg p-4 bg-secondary/30 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Expected Sales</span><span className="font-semibold">{money(viewRow.expected_sales)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cash Collected</span><span className="font-semibold">{money(viewRow.cash_collected)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Online Collected</span><span className="font-semibold">{money(viewRow.online_collected || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Collected</span><span className="font-semibold">{money(Number(viewRow.cash_collected || 0) + Number(viewRow.online_collected || 0))}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Expenses</span><span className="font-semibold">{money(viewRow.total_expenses)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cash to Submit</span><span className="font-semibold">{money(viewRow.cash_to_submit)}</span></div>
                <div className="flex justify-between border-t pt-1.5"><span className="text-muted-foreground">Short / Over</span><ShortOver value={viewRow.short_over} /></div>
              </div>

              {viewRow.notes && <div className="text-sm"><span className="text-muted-foreground">Notes: </span>{viewRow.notes}</div>}

              {(() => { const ph = (viewRow.meter_photos && viewRow.meter_photos.length) ? viewRow.meter_photos : (viewRow.meter_photo ? [viewRow.meter_photo] : []); return ph.length ? (
                <div>
                  <div className="text-sm font-medium mb-1.5">Meter Photos</div>
                  <div className="flex gap-2 flex-wrap">{ph.map((p, i) => <button key={i} type="button" onClick={() => setViewPhoto(p)}><img src={p} alt="meter" className="h-24 w-24 rounded-md object-cover border hover:ring-2 hover:ring-primary" /></button>)}</div>
                </div>
              ) : null; })()}

              {viewRow.status !== "approved" && (
                <div className="flex justify-end pt-1">
                  <Button onClick={() => { approve(viewRow.id); setViewRow(null); }} data-testid="db-view-approve"><SealCheckIcon size={16} className="mr-1.5" />Approve & Add to Revenue</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
